import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ListingsFilterMaterialSelection } from '@steelbuy/form';
import {
    AddressState,
    Country,
    GaugeMapType,
    getDimensionUnit,
    getFilterFields,
    getGaugeMap,
    MaterialProperties,
    Quality,
    TranslateFunc,
} from '@steelbuy/ts-shared';
import {
    filterInputs as sharedFilterInputs,
    generatePermutations,
    getThicknessTypes,
    nonMaterialFilters as sharedNonMaterialFilters,
    SortComparator,
} from '@steelbuy/util';
import { DashboardFormData } from './DashboardFormContext';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = any;

type FilterInput = Partial<
    Record<
        keyof DashboardFormData,
        {
            getOptions: (material: MaterialProperties, prime?: boolean) => Option;
            fixedLabelMapper?: (t: TranslateFunc) => string;
            labelMapper?: (option: Option, t: TranslateFunc) => string;
            valueMapper?: (option: Option) => string | number | { min: number; max: number };
            sort?: SortComparator;
        }
    >
>;

// Dashboard-only filter entries merged with the shared config
const dashboardOnlyFilterInputs: FilterInput = {
    thicknessType: { getOptions: getThicknessTypes },
    quality: {
        getOptions: () => [Quality.PRIME, Quality.EXCESS, Quality.SECONDARY],
        sort: () =>
            // Maintain the order as defined in getOptions
            1,
    },
    location: {
        getOptions: () => Object.values(AddressState).sort((a, b) => a.localeCompare(b)),
        fixedLabelMapper: (t) => t('translation:application.dashboardListings.tableHeadings.location'),
        labelMapper: (option, t) => {
            const state = t(`domainModel:address.shortState.${option as AddressState}`);
            const country = t(`domainModel:address.shortCountry.${Country.US}`);
            return `${state}, ${country}`;
        },
    },
    gauge: {
        getOptions: getGaugeMap,
        fixedLabelMapper: (t) => t('translation:application.dashboardListings.filter.gaugeThickness'),
        labelMapper: (option, t) => {
            const thicknessRange = `${t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                value: option.min,
            })} - ${t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                value: option.max,
            })}`;
            const plateProductOptions = option?.label
                ? `${t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                      value: option.min,
                  })} (${option.label} )`
                : undefined;
            const gaugeLabel = option.gauge ? `${option.gauge} (${thicknessRange})` : thicknessRange;
            return plateProductOptions ?? gaugeLabel;
        },
        valueMapper: (option: GaugeMapType) => JSON.stringify(option),
        sort: (a, b) => (b as GaugeMapType).min - (a as GaugeMapType).min,
    },
};

const filterInputs: FilterInput = { ...sharedFilterInputs, ...dashboardOnlyFilterInputs };

const nonMaterialFilters = sharedNonMaterialFilters;

export const DashboardFilter = () => {
    const { watch } = useFormContext();
    const formValues = watch();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { sort, ...materialProperties } = formValues;
    const filteredMaterialProperties = Object.fromEntries(
        Object.entries(materialProperties).filter(([key]) => !nonMaterialFilters.includes(key))
    );
    const materialPermutations = useMemo(
        () => generatePermutations(filteredMaterialProperties),
        [JSON.stringify(filteredMaterialProperties)]
    );

    const filterInputsWithOptions = useMemo(() => {
        const inputs = getFilterFields({
            materialType: materialProperties.materialType,
            definition: materialProperties.definition,
        })
            .filter((field) => filterInputs[field as keyof typeof filterInputs] !== undefined)
            .map((field) => ({
                ...filterInputs[field as keyof typeof filterInputs],
                field,
            }));
        return inputs.map((input) => ({
            ...input,
            options: Array.from(new Set(materialPermutations.flatMap((p) => input.getOptions?.(p, false)))).sort(),
        }));
    }, [materialPermutations]);

    return (
        <div className="dashboard-filter">
            {filterInputsWithOptions.map((input) => (
                <ListingsFilterMaterialSelection
                    key={input.field}
                    name={input.field}
                    options={input.options}
                    fixedLabelMapper={input.fixedLabelMapper}
                    labelMapper={input.labelMapper}
                    valueMapper={input.valueMapper}
                    sort={input.sort}
                />
            ))}
        </div>
    );
};
